import React, { useState, useEffect, useRef } from "react";
import { RLink } from "../DesignSystem/RLink";
import { api } from "../api";
import type { EntryJSON } from "../models/entries";
import { AvatarStack } from "../DesignSystem/AvatarStack";
import { queueSong } from "../state";

export type PlaylistSong = {
  entry_id: number;
  name: string;
  uploaded_url: string;
  entry_url: string;
  artists?: string[];
};

interface EntryUser {
  username: string;
  avatar_url: string;
}

interface PlaylistPopoverProps {
  songs: PlaylistSong[];
  currentIndex?: number;
  onSongClick?: (index: number) => void;
  onRemove?: (index: number) => void;
  title?: string;
  showAddRandom?: boolean;
}

export function PlaylistPopover({
  songs,
  currentIndex,
  onSongClick,
  onRemove,
  title = "Playlist",
  showAddRandom = false,
}: PlaylistPopoverProps): JSX.Element {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [entryData, setEntryData] = useState<Map<number, EntryJSON>>(new Map());
  const [entryUsers, setEntryUsers] = useState<Map<number, EntryUser[]>>(
    new Map()
  );
  const [randomSuggestion, setRandomSuggestion] = useState<EntryJSON | null>(
    null
  );

  // Fetch entry data for songs
  useEffect(() => {
    const fetchEntryData = async () => {
      const entryIds = songs
        .map((song) => song.entry_id)
        .filter((id) => id && !entryData.has(id));

      if (entryIds.length === 0) return;

      try {
        const entries = await Promise.all(
          entryIds.map((id) => api.get<"/api/entry/:id">(`/api/entry/${id}`))
        );

        const newEntryData = new Map(entryData);
        const newEntryUsers = new Map(entryUsers);

        entries.forEach((result) => {
          if (!api.isToast(result) && result.entry) {
            newEntryData.set(result.entry.id, result.entry);

            const entryUserData = result.entry_users
              .filter((user) => user.id !== -1)
              .map((user) => ({
                username: user.username,
                avatar_url: user.avatar_url,
              }));
            newEntryUsers.set(result.entry.id, entryUserData);
          }
        });

        setEntryData(newEntryData);
        setEntryUsers(newEntryUsers);
      } catch (error) {
        console.error("Failed to fetch entry data:", error);
      }
    };

    fetchEntryData();
  }, [songs]);

  // Fetch random suggestion
  useEffect(() => {
    if (!showAddRandom) return;

    const fetchRandomSuggestion = async () => {
      try {
        const result = await api.get<"/api/random">("/api/random", {
          params: { likes: "5", type: "song" },
        });
        if (result.entry_id !== null) {
          const entryResult = await api.get<"/api/entry/:id">(
            `/api/entry/${result.entry_id}`
          );
          if (!api.isToast(entryResult)) {
            setRandomSuggestion(entryResult.entry);
          }
        }
      } catch (error) {
        console.error("Failed to fetch random suggestion:", error);
      }
    };

    fetchRandomSuggestion();
  }, [showAddRandom]);

  const addRandomTrack = async () => {
    try {
      const result = await api.get<"/api/random">("/api/random", {
        params: { likes: "5", type: "song" },
      });
      if (result.entry_id !== null) {
        const entryResult = await api.get<"/api/entry/:id">(
          `/api/entry/${result.entry_id}`
        );
        if (!api.isToast(entryResult) && entryResult.entry) {
          const artistUsernames = entryResult.entry_users
            .filter((u) => u.id !== -1)
            .map((u) => u.username);
          queueSong({
            name: entryResult.entry.title,
            uploaded_url: entryResult.entry.uploaded_url!,
            entry_url: `/entry/${entryResult.entry.id}`,
            entry_id: entryResult.entry.id,
            artists: artistUsernames.length > 0 ? artistUsernames : undefined,
          });

          setTimeout(() => {
            if (scrollRef.current) {
              scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
            }
          }, 100);
        }
      }
    } catch (error) {
      console.error("Failed to add random track:", error);
    }
  };

  const hasEntries = songs.some(
    (song) => song.entry_id && entryData.has(song.entry_id)
  );

  return (
    <div
      ref={scrollRef}
      className="max-h-[400px] w-[500px] overflow-y-auto rounded-2xl border border-slate-200 bg-white p-4 shadow-2xl backdrop-blur dark:border-white/10 dark:bg-slate-900/95"
    >
      <div className="mb-4">
        <span className="text-sm font-semibold text-slate-900 dark:text-white">
          {title}
        </span>
      </div>
      {!hasEntries ? (
        <div className="px-4 py-8 text-center text-sm">
          <div className="mb-2 text-slate-500 dark:text-slate-400">
            {showAddRandom ? "Your queue is empty." : "No songs in tracklist."}
          </div>
          {showAddRandom && randomSuggestion && (
            <div className="text-slate-700 dark:text-slate-300">
              Why not add this track?{" "}
              <RLink
                to={`/entry/${randomSuggestion.id}`}
                className="font-semibold text-chips-gold hover:text-amber-400"
              >
                {randomSuggestion.title}
              </RLink>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <div className="space-y-2">
            {songs.map((song, index) => {
              const entry = entryData.get(song.entry_id);
              if (!entry) return null;

              const avatarUsersForSong = entryUsers.get(song.entry_id) || [];
              const isCurrentSong = currentIndex === index;

              return (
                <div
                  key={`${song.entry_id}-${index}`}
                  className={`group flex cursor-pointer items-center gap-3 rounded-lg p-2 transition ${
                    isCurrentSong
                      ? "bg-blue-50 dark:bg-blue-900/30"
                      : "hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  }`}
                  onClick={() => onSongClick?.(index)}
                >
                  <span
                    className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-xs font-medium ${
                      isCurrentSong
                        ? "bg-blue-600 text-white"
                        : "bg-slate-200 text-slate-600 dark:bg-slate-600 dark:text-slate-300"
                    }`}
                  >
                    {index + 1}
                  </span>

                  <AvatarStack
                    users={avatarUsersForSong}
                    size="small"
                    linkToProfiles
                  />

                  <div className="min-w-0 flex-1">
                    <RLink
                      to={`/entry/${entry.id}`}
                      className="block text-sm font-medium text-slate-900 hover:text-chips-gold dark:text-white"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="truncate">{entry.title}</div>
                    </RLink>
                    {entry.hearts > 0 && (
                      <div className="mt-0.5 flex items-center gap-1 text-xs text-slate-500 dark:text-slate-400">
                        <i className="fa fa-heart text-red-500"></i>
                        {entry.hearts}
                      </div>
                    )}
                  </div>

                  {onRemove && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemove(index);
                      }}
                      className="inline-flex h-6 w-6 items-center justify-center rounded-full text-slate-400 opacity-0 transition-opacity hover:bg-slate-200 hover:text-slate-600 group-hover:opacity-100 dark:hover:bg-slate-700 dark:hover:text-slate-300"
                      aria-label="Remove from playlist"
                    >
                      <i className="fa fa-times text-sm" aria-hidden="true"></i>
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {showAddRandom && (
            <button
              type="button"
              onClick={addRandomTrack}
              className="w-full rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 hover:text-chips-gold dark:border-white/10 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-chips-gold"
            >
              <i className="fa fa-random mr-2" aria-hidden="true"></i>
              Add random track
            </button>
          )}
        </div>
      )}
    </div>
  );
}
